import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { UntypedFormGroup } from '@angular/forms';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { PackageInterface } from '@vendasta/marketplace-packages';
import { MarketplaceService } from '../../services/marketplace.service';
import { FooterCtaConfigInterface } from '@galaxy/snapshot';

const URL_REGEX = new RegExp(/^((https?:\/\/)?([\w-]+(\.[\w-]+)+|localhost)\.?(:\d+)?((\/)?\S*)?)$/i);

export enum ctaTargetOptions {
  CONTACT_FORM = 0,
  PRODUCT_ID = 1,
  URL = 2,
}

@Component({
  selector: 'app-edit-button-dialog',
  templateUrl: './edit-button-dialog.component.html',
  styleUrls: ['./edit-button-dialog.component.scss'],
})
export class EditButtonDialogComponent {
  selectedCtaTargetOption: number;

  defaultLabel: string;
  label: string;
  targetUrl: string;
  targetProduct: PackageInterface;

  formGroup: UntypedFormGroup;

  marketplaceProducts$$ = new ReplaySubject<PackageInterface[]>(1);
  loading$$ = new ReplaySubject<boolean>(1);

  isUrlInvalid = false;
  isProductInvalid = false;
  isProductAvailable = true;

  constructor(
    public dialogRef: MatDialogRef<EditButtonDialogComponent>,
    private readonly marketplaceService: MarketplaceService,
    @Inject(MAT_DIALOG_DATA) public data: FooterCtaConfigInterface,
  ) {
    this.defaultLabel = data.defaultLabel;
    this.label = data.label;
    this.targetUrl = data.targetUrl;
    this.formGroup = data.formGroup;
    this.initAsyncValues();
  }

  private async initAsyncValues(): Promise<void> {
    this.loading$$.next(true);
    await this.setupForm();
    this.loading$$.next(false);
  }

  private async setupForm(): Promise<void> {
    const marketplaceProducts = await firstValueFrom(this.marketplaceService.activatablePackages$);
    this.marketplaceProducts$$.next(marketplaceProducts);

    const targetProductId = this.data.targetProductId;
    const targetProduct = marketplaceProducts.find((s) => s.packageId === targetProductId);
    if (targetProductId && targetProduct) {
      this.selectedCtaTargetOption = ctaTargetOptions.PRODUCT_ID;
      this.targetProduct = targetProduct;
    } else if (this.targetUrl) {
      this.selectedCtaTargetOption = ctaTargetOptions.URL;
    } else {
      this.selectedCtaTargetOption = ctaTargetOptions.CONTACT_FORM;
      this.isProductAvailable = !targetProductId;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    let targetUrl = this.targetUrl;
    if (targetUrl !== '' && !targetUrl.startsWith('https://') && !targetUrl.startsWith('http://')) {
      targetUrl = 'https://' + targetUrl;
    }
    let packageId = '';
    if (this.targetProduct) {
      packageId = this.targetProduct.packageId;
    }
    this.formGroup.setValue({
      customizedFooterCtaLabel: this.label,
      customizedFooterCtaTargetUrl: targetUrl,
      customizedFooterCtaTargetProduct: packageId,
    });
    this.dialogRef.close();
  }

  async navigateToURL(): Promise<void> {
    if (this.selectedCtaTargetOption === ctaTargetOptions.PRODUCT_ID) {
      const url = await firstValueFrom(this.marketplaceService.packageUrl$(this.targetProduct.packageId));
      window.open(url, '_blank', 'noopener');
    }
    if (this.selectedCtaTargetOption === ctaTargetOptions.URL) {
      window.open(this.targetUrl, '_blank', 'noopener');
    }
  }

  onFocusTargetUrlInput(): void {
    this.isProductAvailable = true;
    this.selectedCtaTargetOption = ctaTargetOptions.URL;
    this.targetProduct = { packageId: '', name: '', icon: '' };
  }

  resetNoSelectedTargetCtaOptions(event: MatRadioChange): void {
    // To hide error message of previous selected product is not valid
    this.isProductAvailable = true;
    this.validateSelectedProduct(event.value);
    this.validateCustomUrl(event.value);
    switch (event.value) {
      case ctaTargetOptions.URL:
        this.targetProduct = { packageId: '', name: '', icon: '' };
        break;
      case ctaTargetOptions.PRODUCT_ID:
        this.targetUrl = '';
        break;
      case ctaTargetOptions.CONTACT_FORM:
        this.targetProduct = { packageId: '', name: '', icon: '' };
        this.targetUrl = '';
        break;
    }
  }

  validateCustomUrl(selected: number): void {
    if (selected === undefined) {
      return;
    }
    const invalidUrl = !URL_REGEX.test(this.targetUrl);
    this.isUrlInvalid = selected === ctaTargetOptions.URL && invalidUrl;
  }

  validateSelectedProduct(selected: number): void {
    this.isProductInvalid =
      selected === ctaTargetOptions.PRODUCT_ID &&
      (this.targetProduct === undefined || this.targetProduct.packageId === '')
        ? true
        : false;
  }
}
